import {ReplaySubject, Subject} from 'rxjs';
import {SuperAggregatorDashboardModel} from '../../shared/models/super-aggregator.model';
import {UserAlias} from '../../shared/constants';
import {StringHelper} from '../helpers/strings.helper';
import {StorageKeys} from '../../shared/models/storage-keys';
import {LoginRequestModel,LoginV2RequestModel} from '../../shared/models/login.model';
import {ApiClientHelper} from '../helpers/api-client.helper';
import {LocalStorageService} from './local-storage.service';
import {Injectable} from '@angular/core';
import {ApiResponse} from '../api/api-response';
import {AppConfig} from '../../shared/config/app-config';
import {AuthorizedUser} from './authorized-user.service';
import {Survey} from '../../shared/models/utility.model';
import {DeviceHelperService} from './device-helper.service';
import {MobileWebViewService} from './mobile-web-view.service';
import { logger } from '@mpt-fo/common/telemetry';

@Injectable()
export class AuthenticationService {
	loggedInUser: any;
	monitorGrafanaUrl: Subject<string> = new ReplaySubject();
	featureAccess: Subject<string[]> = new ReplaySubject(1);
	surveyData: Subject<Survey[]> = new ReplaySubject<Survey[]>(1);
	coordinatorState: Subject<string> = new ReplaySubject<string>(1);
	dashboardData: any = new ReplaySubject(1);
	lastAttemptedUrl: string;
	private mockAuthenticationService: MockAuthenticationService;

	constructor(
		private apiClientHelper: ApiClientHelper,
		private localStorage: LocalStorageService,
		private authorizedUser: AuthorizedUser,
		private deviceHelper: DeviceHelperService,
		private mobileService: MobileWebViewService,
		public config: AppConfig
	) {
		let featureAccess = localStorage.get(StorageKeys.feature_access);
		featureAccess = featureAccess ? JSON.parse(featureAccess) : [];
		const injectedFeatureAccess = this.addInjectedFeatureAccess();
		featureAccess = [...featureAccess, ...injectedFeatureAccess];
		featureAccess = JSON.stringify(featureAccess);
		if (featureAccess) {
			try {
				this.setFeatureAccess(JSON.parse(featureAccess), false);
			} catch (error) {
				logger.error('error parsing feature access ', featureAccess);
			}
		}
		this.mockAuthenticationService = new MockAuthenticationService(config);
	}

	get userPermissions(): string[] {
		const token = this.getDecodedToken();
		let permissions = [];
		if (token && token.authorities) {
			permissions = token.authorities;
		}
		return permissions;
	}

	get token() {
		return this.localStorage.get(StorageKeys.token);
	}

	getContextUser() {
		const role = this.localStorage.get('selected_role');
		switch (role) {
            case 'MONNIFY_AGENCY_BANKING_AGENT':
            case 'MONNIFY_AGENCY_BANKING_GENERIC_STAFF_USER':
                return 'agents';
            case UserAlias.aggregator:
                return 'aggregators';
            case UserAlias.subBRM:
                return 'subbrm';
            case UserAlias.areaManager:
                return 'area-manager';
            case UserAlias.superAggregator:
                return 'super-aggregators';
            case UserAlias.supremeAggregator:
                return 'supreme-aggregator';
            case UserAlias.clusterManager:
                return 'cluster-manager';
            case 'OUTLET_MANAGER':
            case 'MONNIFY_AGENT_MANAGER':
                return 'agent-managers';
        }
	}

	getDecodedToken(token?: string): any {
		if (token) {
			return StringHelper.decodeJWT(token);
		} else {
			token = localStorage.getItem(StorageKeys.token);
			if (token) {
				return StringHelper.decodeJWT(localStorage.getItem(StorageKeys.token));
			} else {
				return token;
			}
		}
	}

	setFeatureAccess(features, saveFeatureAccess) {
		if (saveFeatureAccess) {
			this.localStorage.save(StorageKeys.feature_access, JSON.stringify(features));
		}
		const injectedFeatureAccess = this.addInjectedFeatureAccess();
		features = [...features, ...injectedFeatureAccess];
		this.featureAccess.next(features);
	}

	addInjectedFeatureAccess() {
		const featureAccess = [];
		if (this.config.SHOW_CAPITAL_LOANS_MENU) {
			featureAccess.push('show_capital_loans_menu');
		}
		return featureAccess;
	}

	isLoggedIn(): boolean {
		const accessToken = this.localStorage.get(StorageKeys.token);
		return StringHelper.isValidString(accessToken);
	}

	login(request: LoginRequestModel): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/tokens`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	logout(): Promise<any> {
		const username = this.localStorage.getSaveTokenData()?.username;
		const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/logout/?username=${username}`;
		return this.apiClientHelper.getForPromise(url);
	}

	validatePin(request: { pin: string }): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/profiles/agents/validate-pin`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	postLivelinessCheck(param): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/agent-managers/liveness-check`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, param);
	}

	doLivelinessCheckWithoutPin(param): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/agent-managers/no-pin-liveness-check`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, param, false);
	}

	loginFD(request: LoginRequestModel, clientType = 'WEB', checkAuth = true): Promise<any> {
		const url = this.getLoginUrl();
		return this.apiClientHelper.loginPostForPromise(url, request, clientType, checkAuth);
	}

	loginV2FD(request: LoginV2RequestModel, clientType = 'WEB', checkAuth = true): Promise<any> {
		const url = this.getLoginUrl();
		return this.apiClientHelper.loginPostForPromise(url, request, clientType, checkAuth);
	}

	loginGetUserType(params): Promise<any> {
		const url = this.config.MONIEPOINT_USER_URL + '/api/v2/auth/login-type';
		return this.apiClientHelper.loginUserTypeGetForPromise(url, params);
	}

	getLoginUrl(oldUrl?){
		oldUrl = oldUrl ?? '/api/v1/auth/tokens';
		const url = this.config.USE_NEW_LOGIN_API || this.config.SHOW_SIGN_UP_PASSCODE_FLOW ? '/api/v2/auth/token' : oldUrl;
		return this.config.MONIEPOINT_HOST + url;
	}

	WebViewLogin(request: LoginRequestModel, clientType = 'WEB', checkAuth = true, isMobileUser: boolean): Promise<any> {
		if (!isMobileUser) {
			return this.loginFD(request);
		}
		const url = this.getLoginUrl(`/api/v1/auth/tokens-v2`);
		return this.apiClientHelper.loginPostForPromise(url, request, clientType, checkAuth, this.mobileService.getMobileAppVersion());
	}

	activateDeviceOnLogin(request): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/user-device/activate-auth`;
		return this.apiClientHelper.postForPromise(url, request, false);
	}

  activateDeviceOnLoginV2(request): Promise<any> {
    const url = this.config.MONIEPOINT_USER_URL + `/api/v2/user-devices/activate-auth`;
    return this.apiClientHelper.postForPromise(url, request, false);
  }


	forgotPassword(request): Promise<any> {
		const params = StringHelper.toUrlString(request);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/forgot?${params}`;
		return this.apiClientHelper.getForPromise(url, true);
	}

	recoverUsernameUssd(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-username/ussd-code`;
		return this.apiClientHelper.getForPromise(url, true);
	}

	generatePasswordOTP(data): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-password/otp/generate`;
		return this.apiClientHelper.postForPromise(url, data, false);
	}

	validatePasswordOTP(data): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-password/otp/validate`;
		return this.apiClientHelper.postForPromise(url, data, false);
	}

	changePassword(data): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-password`;
		return this.apiClientHelper.postForPromise(url, data, false);
	}

	doFaceMatchCheck(param): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/auth/recover-password/liveliness`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, param, false);
	}

	triggerOtpForUsernameRetrieval(data): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/send-forget-username-otp`;
		return this.apiClientHelper.postForPromise(url, data, false);
	}

	getDashboardData(): Promise<SuperAggregatorDashboardModel> {
		let context = this.getContextUser();
		if (context === 'area-manager') {
			context = 'aggregators';
		}
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/dashboard/${context}?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}
}

class MockAuthenticationService {
	loggedInUser: any;

	constructor(public config: AppConfig) {
	}

	login(email: string, password: string): Promise<any> {
		return new Promise((resolve, reject) => {
			this.loggedInUser = {username: email, password};

			setTimeout(() => {
				this.loggedInUser = {username: email, password};

				resolve(new ApiResponse({}));
			}, 1000);
		});
	}

	ping(): Promise<any> {
		return new Promise((resolve, reject) => {
			resolve(new ApiResponse({success: true, result: {}}));
		});
	}

	logout(): Promise<any> {
		return new Promise((resolve, reject) => {
			resolve(new ApiResponse({success: true}));
		});
	}
}

// Generated by https://quicktype.io

export interface LoginResponseModel {
	username: string;
	firstName: string;
	lastName: string;
	email: string;
	roles: string[];
	authorities: string[];
	access_token: string;
	expires_in: number;
}
